/**
 * Format a UTC ISO string as a full local datetime in the user's stored timezone.
 * Example output: "03 Apr 2026, 12:53 AM"
 *
 * @param {string|Date} isoString
 * @param {string} timezone - IANA tz name from user profile, e.g. "Africa/Accra"
 * @returns {string}
 */
export function formatDateTime(isoString, timezone = 'UTC') {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(new Date(isoString));
}

/**
 * Format a UTC ISO string as a date only in the user's stored timezone.
 * Example output: "03 Apr 2026"
 *
 * @param {string|Date} isoString
 * @param {string} timezone - IANA tz name from user profile
 * @returns {string}
 */
export function formatDate(isoString, timezone = 'UTC') {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(isoString));
}

/**
 * Get today's date as a "yyyy-MM-dd" string in the user's stored timezone.
 * Uses the real wall clock but applies the user's profile timezone — not the device timezone.
 *
 * @param {string} timezone - IANA tz name from user profile
 * @returns {string} e.g. "2026-04-03"
 */
export function todayInTimezone(timezone = 'UTC') {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

/**
 * Get a date N days ago as a "yyyy-MM-dd" string in the user's stored timezone.
 *
 * @param {number} daysAgo
 * @param {string} timezone - IANA tz name from user profile
 * @returns {string} e.g. "2026-03-27"
 */
export function daysAgoInTimezone(daysAgo, timezone = 'UTC') {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

/**
 * Resolve a named period to a { startDate, endDate } pair of "yyyy-MM-dd"
 * strings, anchored to *today in the user's timezone*.
 *
 * Week / month / year use CALENDAR boundaries (this week from Monday, this
 * month from the 1st, this year from Jan 1) — the intuitive reading of those
 * labels — up to and including today. Once "today" is resolved in the user's
 * timezone, the boundaries are pure calendar arithmetic, so no further
 * timezone juggling is needed.
 *
 * @param {'today'|'yesterday'|'week'|'month'|'year'} period
 * @param {string} timezone - IANA tz name from the user profile
 * @returns {{ startDate: string, endDate: string }}
 */
export function periodRangeInTimezone(period, timezone = 'UTC') {
  const today = todayInTimezone(timezone);           // "yyyy-MM-dd" in tz
  const [y, m, d] = today.split('-').map(Number);
  // A Date at LOCAL midnight for that calendar date. We only read calendar
  // fields off it (getDay/getMonth/date arithmetic), which are tz-independent
  // for a midnight-anchored date, so the device tz never enters the result.
  const anchor = new Date(y, m - 1, d);
  const ymd = (dt) =>
    `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;

  switch (period) {
    case 'today':
      return { startDate: today, endDate: today };
    case 'yesterday': {
      const yst = new Date(anchor);
      yst.setDate(yst.getDate() - 1);
      return { startDate: ymd(yst), endDate: ymd(yst) };
    }
    case 'week': {
      // Monday as the first day of the week.
      const start = new Date(anchor);
      const dow = (start.getDay() + 6) % 7; // 0 = Monday
      start.setDate(start.getDate() - dow);
      return { startDate: ymd(start), endDate: today };
    }
    case 'month':
      return { startDate: `${y}-${String(m).padStart(2, '0')}-01`, endDate: today };
    case 'year':
      return { startDate: `${y}-01-01`, endDate: today };
    default:
      return { startDate: today, endDate: today };
  }
}
